import { useRef, useState, type ReactNode } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, ImagePlus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  FLOOR_STYLES,
  ROOM_REFRESH_SUGGESTIONS,
  RoomRefreshOptions,
  WALL_COLORS,
  WALLPAPERS,
} from "@/lib/room-refresh-options";

const MAX_REFS = 2;
const MAX_MB = 5;

type OptionKey = keyof RoomRefreshOptions;

export interface RoomRefreshInputsProps {
  prompt: string;
  onPromptChange: (v: string) => void;
  references: string[];
  onReferencesChange: (refs: string[]) => void;
  options: RoomRefreshOptions;
  onOptionsChange: (options: RoomRefreshOptions) => void;
  compact?: boolean;
  /** Two-column side-by-side layout on wide screens (web modal). */
  wide?: boolean;
}

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * Shared prompt + optional reference-image picker for Room Refresh.
 * Used by the web modal and the mobile screen so the input UX stays identical.
 *
 * The three "Choose a new look" categories are individually collapsible and
 * collapsed by default, so the form stays compact; a selection is always
 * visible in the section header even while collapsed.
 *
 * Reference images are capped at 2, 5 MB each. They are kept as data URLs here
 * and uploaded to storage at submit time by the caller.
 */
export const RoomRefreshInputs = ({
  prompt,
  onPromptChange,
  references,
  onReferencesChange,
  options,
  onOptionsChange,
  compact,
  wide,
}: RoomRefreshInputsProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<Record<OptionKey, boolean>>({
    wallColor: true,
    floorStyle: false,
    wallpaper: false,
  });

  const toggleSection = (key: OptionKey) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);
    const remaining = MAX_REFS - references.length;
    if (remaining <= 0) {
      setError(`You can add up to ${MAX_REFS} photos.`);
      return;
    }
    const picked = Array.from(files).slice(0, remaining);
    const next: string[] = [];
    for (const file of picked) {
      if (!file.type.startsWith("image/")) {
        setError("Only image files are allowed.");
        continue;
      }
      if (file.size > MAX_MB * 1024 * 1024) {
        setError(`Each image must be ${MAX_MB} MB or smaller (${file.name} is too large).`);
        continue;
      }
      try {
        next.push(await fileToDataUrl(file));
      } catch {
        setError(`Could not read ${file.name}.`);
      }
    }
    if (next.length > 0) onReferencesChange([...references, ...next]);
  };

  const removeRef = (idx: number) => {
    onReferencesChange(references.filter((_, i) => i !== idx));
  };

  const chooseOption = (key: OptionKey, value: string) => {
    onOptionsChange({ ...options, [key]: options[key] === value ? null : value });
  };

  const sectionHeader = (
    key: OptionKey,
    title: string,
    selected: string | null,
    onClear: () => void,
  ) => {
    const open = openSections[key];
    return (
      <div className="mb-2 flex min-h-7 items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => toggleSection(key)}
          aria-expanded={open}
          aria-controls={`rr-section-${key}`}
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md text-left text-sm font-medium transition-colors hover:text-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
              !open && "-rotate-90"
            )}
          />
          <span className="truncate">
            {title}
            {selected && !open ? ` — ${selected}` : ""}
          </span>
        </button>
        {selected && (
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>
    );
  };

  const collapsibleBody = (key: OptionKey, children: ReactNode) => {
    const open = openSections[key];
    return (
      <div
        id={`rr-section-${key}`}
        className={cn(
          "grid transition-all duration-200 ease-in-out",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          {children}
        </div>
      </div>
    );
  };

  return (
    <div className={cn("space-y-6", wide && "lg:grid lg:grid-cols-2 lg:items-start lg:gap-8 lg:space-y-0")}>
      <section aria-labelledby="room-refresh-look-heading">
        <div className="mb-4">
          <h3 id="room-refresh-look-heading" className="text-base font-semibold">Choose a new look</h3>
          <p className="text-xs text-muted-foreground">Optional — pick anything you like</p>
        </div>

        <div className="space-y-4">
          <div>
            {sectionHeader("wallColor", "Wall Color", options.wallColor, () => onOptionsChange({ ...options, wallColor: null }))}
            {collapsibleBody(
              "wallColor",
              <div className="grid grid-cols-5 gap-2">
                {WALL_COLORS.map((item) => {
                  const selected = options.wallColor === item.name;
                  return (
                    <Button
                      key={item.name}
                      type="button"
                      variant="outline"
                      aria-pressed={selected}
                      aria-label={`${item.name}${selected ? ", selected" : ""}`}
                      onClick={() => chooseOption("wallColor", item.name)}
                      className={cn(
                        "h-auto min-w-0 flex-col gap-1.5 rounded-lg border p-1.5 text-[10px] leading-tight whitespace-normal",
                        selected && "border-primary bg-primary/5 ring-2 ring-primary/20"
                      )}
                    >
                      <span className={cn("relative block aspect-square w-full rounded-md border", item.swatchClass)}>
                        {selected && <Check className="absolute inset-0 m-auto h-4 w-4 drop-shadow-sm" />}
                      </span>
                      <span className="line-clamp-2 min-h-6">{item.name}</span>
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            {sectionHeader("floorStyle", "Choose a floor or tile style", options.floorStyle, () => onOptionsChange({ ...options, floorStyle: null }))}
            {collapsibleBody(
              "floorStyle",
              <div className="grid grid-cols-5 gap-2">
                {FLOOR_STYLES.map((item) => {
                  const selected = options.floorStyle === item.name;
                  return (
                    <Button
                      key={item.name}
                      type="button"
                      variant="outline"
                      aria-pressed={selected}
                      onClick={() => chooseOption("floorStyle", item.name)}
                      className={cn(
                        "h-auto min-w-0 flex-col gap-1.5 rounded-lg border p-1.5 text-[10px] leading-tight whitespace-normal",
                        selected && "border-primary bg-primary/5 ring-2 ring-primary/20"
                      )}
                    >
                      <span className="relative block aspect-square w-full overflow-hidden rounded-md">
                        <img src={item.image} alt="" loading="lazy" width={320} height={320} className="h-full w-full object-cover" />
                        {selected && <span className="absolute inset-0 grid place-items-center bg-background/35"><Check className="h-4 w-4" /></span>}
                      </span>
                      <span className="line-clamp-2 min-h-6">{item.name}</span>
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            {sectionHeader("wallpaper", "Wallpaper", options.wallpaper, () => onOptionsChange({ ...options, wallpaper: null }))}
            {collapsibleBody(
              "wallpaper",
              <div className="grid grid-cols-5 gap-2">
                {WALLPAPERS.map((item) => {
                  const selected = options.wallpaper === item.name;
                  return (
                    <Button
                      key={item.name}
                      type="button"
                      variant="outline"
                      aria-pressed={selected}
                      onClick={() => chooseOption("wallpaper", item.name)}
                      className={cn(
                        "h-auto min-w-0 flex-col gap-1.5 rounded-lg border p-1.5 text-[10px] leading-tight whitespace-normal",
                        selected && "border-primary bg-primary/5 ring-2 ring-primary/20"
                      )}
                    >
                      <span className="relative block aspect-square w-full overflow-hidden rounded-md">
                        <img src={item.image} alt="" loading="lazy" width={320} height={320} className="h-full w-full object-cover" />
                        {selected && <span className="absolute inset-0 grid place-items-center bg-background/35"><Check className="h-4 w-4" /></span>}
                      </span>
                      <span className="line-clamp-2 min-h-6">{item.name}</span>
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="space-y-6">
      <section>
        <div className="mb-3">
          <h3 className="text-sm font-semibold">📷 Want to add something you like?</h3>
          <p className="text-xs text-muted-foreground">Upload a picture of a sofa, chair, lamp, rug, decor or anything else you'd like to see in your room.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {references.map((src, idx) => (
            <div key={idx} className="relative group w-20 h-20 rounded-lg overflow-hidden border">
              <img src={src} alt={`Added photo ${idx + 1}`} className="w-full h-full object-cover" />
              <Button
                type="button"
                variant="secondary"
                size="icon"
                onClick={() => removeRef(idx)}
                className="absolute right-1 top-1 h-6 w-6 rounded-full opacity-90"
                aria-label={`Remove added photo ${idx + 1}`}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          {references.length < MAX_REFS && (
            <Button
              type="button"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              className="h-20 w-28 flex-col gap-1 rounded-lg border-dashed text-muted-foreground"
            >
              <ImagePlus className="w-5 h-5" />
              <span className="text-xs">＋ Add a photo</span>
            </Button>
          )}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Up to {MAX_REFS} photos · {MAX_MB} MB each</p>
        <p className="mt-1 text-xs text-muted-foreground">Sofa, chair, lamp, rug, wall art, plant, decor item, wallpaper, tile design, or any furniture or product you like.</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        {error && <p className="text-xs text-destructive mt-1.5">{error}</p>}
      </section>

      <section>
        <div className="mb-2">
          <label htmlFor="room-refresh-instructions" className="text-sm font-semibold">
            ✨ Have something specific in mind?
          </label>
          <p className="text-xs text-muted-foreground">Tell us what to do with your photo, or describe any change you'd like.</p>
        </div>
        <Textarea
          id="room-refresh-instructions"
          placeholder="e.g. Use the sofa from the attached image in my room."
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          className={`min-h-[108px] resize-none rounded-xl ${compact ? "text-base" : ""}`}
        />
        <div className="mt-2 flex gap-2 overflow-x-auto pb-1 sm:flex-wrap" aria-label="Suggested ideas">
          {ROOM_REFRESH_SUGGESTIONS.map((suggestion) => (
            <Button
              key={suggestion}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onPromptChange(suggestion)}
              className="h-8 shrink-0 rounded-full border-border px-3 text-xs font-normal"
            >
              {suggestion}
            </Button>
          ))}
        </div>
      </section>
      </div>
    </div>
  );
};
